// Who is working this queue, with the one column that explains why somebody is in
// it and still receiving nothing.
//
// `can_be_assigned` is a PERSISTED computed column from `users.role` (migration
// 004), which is trigger-maintained from `user_product_roles` (migration 010),
// which only the Entra sync writes. An Entra app-role change empties an agent out
// of every rotation while their membership and schedule rows sit there looking
// correct — and NOTHING in this console can restore it. So the row says so, in
// words, and names the fix as being somewhere else.
//
// EVERY SENTENCE COMES FROM ../../pages/admin/queue-members.js, which is pure and
// tested (test/web/queue-members.test.js). Nothing here decides what a member
// state means: there is no DOM harness in this repository, so a ternary written
// inline is a ternary nobody can hold to being total, and a member state that fell
// through it would render a blank cell beside a name. A blank cell reads as
// "fine", which is the same silence one level up.
//
// COLOUR CARRIES NOTHING HERE, on the same terms as OnShiftPreview beside it:
// `--color-sunken` is the row hover fill and is byte-identical to slate-100, so
// anything filled with it vanishes on hover. Assignable and not are separated by
// the sentence and by ink weight. Both themes come out of semantic tokens with no
// `dark:` variant.

import { useEffect, useState } from 'react';

import { apiFetch } from '../../api/client.js';
import { consoleErrorMessage } from '../../pages/admin/descriptor-form.js';
import { memberCapability, memberIds, candidateOptions } from '../../pages/admin/queue-members.js';

export default function QueueMembers({ queueId }) {
  const [members, setMembers] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    setMembers(null);
    setError(null);
    Promise.all([
      apiFetch(`/admin/queues/${encodeURIComponent(queueId)}/members`, { signal: controller.signal }),
      // The existing read endpoint, unchanged, and deliberately NOT filtered to
      // `?can_be_assigned=true`: somebody whose Entra role cannot own tickets is
      // still a legitimate thing to put in a queue, and hiding them here would be
      // this console inventing a rule the API has not got. The option says so
      // instead.
      apiFetch('/users', { signal: controller.signal }),
    ])
      .then(([m, u]) => { setMembers(m.data); setCandidates(u.data); })
      .catch((err) => { if (err?.name !== 'AbortError') setError(consoleErrorMessage(err)); });
    return () => controller.abort();
  }, [queueId]);

  async function save(userIds) {
    setSaving(true);
    setError(null);
    try {
      // One PUT carrying the whole membership: the server replaces it in one
      // transaction, so an add and a remove land together and the response is the
      // membership as it now stands rather than the request echoed back.
      const body = await apiFetch(`/admin/queues/${encodeURIComponent(queueId)}/members`, {
        method: 'PUT',
        body: { user_ids: userIds },
      });
      setMembers(body.data);
    } catch (err) {
      setError(consoleErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  if (error && members === null) {
    return (
      <section className="mt-8">
        <h2 className="text-lg font-semibold text-ink">Members</h2>
        <div className="mt-3 rounded-lg border border-line-strong bg-sunken p-3 text-sm text-ink">
          {error}
        </div>
      </section>
    );
  }
  if (members === null) return <p className="mt-8 text-sm text-ink-muted">Loading the members…</p>;

  const ids = memberIds(members);
  const options = candidateOptions(candidates, members);

  return (
    <section className="mt-8">
      <h2 className="text-lg font-semibold text-ink">Members</h2>
      <p className="mt-1 text-sm text-ink-secondary">
        Who is working this queue right now. Whether they <em>can</em> be assigned comes from
        their Entra app role and cannot be changed here.
      </p>

      {error && (
        <div className="mt-3 rounded-lg border border-line-strong bg-sunken p-3 text-sm text-ink">
          {error}
        </div>
      )}

      <ul className="mt-3 divide-y divide-line rounded-lg border border-line bg-surface">
        {members.length === 0 && (
          <li className="px-3 py-2 text-sm text-ink-muted">
            Nobody. This queue collects tickets and assigns none.
          </li>
        )}
        {members.map((member) => {
          const capability = memberCapability(member);
          return (
            // Stacked below `sm`, name-and-reason on one line above it — the same
            // fix OnShiftPreview records: the reasons run to ~90 characters, and
            // letting them win on a narrow row truncated the NAME, which is the
            // only part a lead is scanning for.
            <li
              key={member.id}
              className="flex flex-col gap-1 px-3 py-2 sm:flex-row sm:items-baseline sm:justify-between sm:gap-3"
            >
              <span className="min-w-0">
                <span
                  className={`block truncate text-sm ${capability.assignable ? 'text-ink' : 'text-ink-muted'}`}
                >
                  {member.display_name}
                </span>
                <span className="block truncate text-xs text-ink-muted">{member.email}</span>
              </span>
              <span className="flex items-baseline gap-3 sm:shrink-0">
                <span
                  className={`text-xs sm:max-w-80 sm:text-right ${capability.assignable ? 'text-ink-muted' : 'text-ink-secondary'}`}
                >
                  {capability.text}
                </span>
                <button
                  type="button"
                  className="btn-ghost shrink-0 ring-1 ring-line-strong"
                  disabled={saving}
                  onClick={() => { void save(ids.filter((id) => id !== member.id)); }}
                >
                  Remove
                </button>
              </span>
            </li>
          );
        })}
      </ul>

      <label htmlFor={`add-member-${queueId}`} className="field-label mt-4">
        Add someone
      </label>
      <select
        id={`add-member-${queueId}`}
        className="field-input"
        // Controlled at "" so the control returns to its prompt after each save
        // rather than reading as though the last person picked is still pending.
        value=""
        disabled={saving}
        onChange={(event) => {
          if (event.target.value) void save([...ids, event.target.value]);
        }}
      >
        <option value="">-</option>
        {options.map((option) => (
          <option key={option.id} value={option.id}>{option.label}</option>
        ))}
      </select>

      {/* Said here rather than discovered during an escalation. Removing somebody
          is fully reversible — re-add them and the five-minute sweep drains their
          backlog — which is exactly what makes it safe to do from this screen. */}
      <p className="mt-3 text-xs text-ink-muted">
        Removing somebody stops new tickets reaching them; tickets they already own stay theirs.
        Re-adding them takes effect on the next routing sweep, within five minutes.
      </p>
    </section>
  );
}
